import React, { useState, useEffect } from 'react';
import { Card, Upload, Button, Spin, Alert, Tag, Space, Row, Col, Divider, message, Select, Radio, Progress, Form, Input, InputNumber, Tooltip, Collapse, Image } from 'antd';
import { UploadOutlined, PlusOutlined, FileTextOutlined, SaveOutlined, DeleteOutlined, UndoOutlined, SwapOutlined } from '@ant-design/icons';
import { axiosInstance, API_BASE_URL } from '../../providers/dataProvider';
import { TranslationsField } from '../../components/TranslationsField';
import { DishType, ALL_DISH_CATEGORIES } from '@wellverse/shared-ts';

const IMAGE_DEFAULT_LONG_EDGE = 512;
const IMAGE_JPEG_QUALITY = 0.8;
const IMAGE_MAX_BYTES = 2 * 1024 * 1024;

// Cached dimension from server settings (fetched once per session)
let _cachedMaxDimension: number | null = null;
const fetchImageMaxDimension = async (): Promise<number> => {
  if (_cachedMaxDimension != null) return _cachedMaxDimension;
  try {
    const res = await axiosInstance.get(`${API_BASE_URL}/settings`);
    const visionSettings = res.data?.vision ?? [];
    const dim = visionSettings.find((s: any) => s.key === 'vision_image_max_dimension');
    if (dim?.value != null) {
      _cachedMaxDimension = Number(dim.value);
      return _cachedMaxDimension;
    }
  } catch { /* fallback */ }
  return IMAGE_DEFAULT_LONG_EDGE;
};

const resizeImageFile = (file: File, maxLongEdge: number): Promise<File> =>
  new Promise((resolve) => {
    if (!file.type.startsWith('image/')) return resolve(file);
    const img = new window.Image();
    img.onload = () => {
      const longEdge = Math.max(img.width, img.height);
      if (longEdge <= maxLongEdge && file.type === 'image/jpeg' && file.size <= IMAGE_MAX_BYTES) {
        URL.revokeObjectURL(img.src);
        return resolve(file);
      }
      const scale = longEdge > maxLongEdge ? maxLongEdge / longEdge : 1;
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(img.src);
          if (!blob) return resolve(file);
          resolve(new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }));
        },
        'image/jpeg',
        IMAGE_JPEG_QUALITY,
      );
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });

const fileToBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve((reader.result as string).replace(/^data:[^;]+;base64,/, ''));
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const resizeAndEncode = async (file: File, maxLongEdge: number): Promise<string> => {
  const resized = await resizeImageFile(file, maxLongEdge);
  return fileToBase64(resized);
};

interface Candidate {
  id: string;
  name: string;
  confidence: number;
  source?: string;
  matchMethod: 'IMAGE' | 'TEXT';
  imageSimilarity?: number;
  category?: string;
  imageUrl?: string;
}

interface CatalogDetail {
  id: string;
  name: string;
  category: string;
  images: Array<{ id: string; imageUrl: string }>;
  calories?: number;
  protein?: number;
  fat?: number;
  carbs?: number;
  fiber?: number;
  sodium?: number;
  standardPortionGrams?: number;
  type?: string;
  ingredients?: Array<{ displayName: string; weightGrams: number }>;
  translations?: Record<string, { name?: string; description?: string }>;
}

interface ScanResult {
  candidates: Candidate[];
  aiSuggestion: {
    name: string;
    chineseName?: string;
    chineseNameTraditional?: string;
    chineseNameSimplified?: string;
    category: string;
    type: DishType;
    standardPortionGrams: number;
    calories: number;
    protein: number;
    fat: number;
    carbs: number;
    fiber: number;
    sodium: number;
    ingredients: Array<{ name: string; estimatedGrams: number }>;
    imageUrl: string;
  };
}

interface LabelResult {
  productName: string;
  chineseName: string;
  chineseNameTraditional?: string;
  chineseNameSimplified?: string;
  category: string;
  type: string;
  servingSize: string;
  calories: number;
  protein: number;
  fat: number;
  carbs: number;
  fiber: number;
  sodium: number;
  sugar: number;
  ingredients: string;
  allergens: string;
  confidence: number;
}

type Step = 'idle' | 'analyzing' | 'results' | 'comparison' | 'edit-form';

const COMPARABLE_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'category', label: 'Category' },
  { key: 'type', label: 'Type' },
  { key: 'standardPortionGrams', label: 'Portion (g/ml)' },
  { key: 'calories', label: 'Calories' },
  { key: 'protein', label: 'Protein (g)' },
  { key: 'fat', label: 'Fat (g)' },
  { key: 'carbs', label: 'Carbs (g)' },
  { key: 'fiber', label: 'Fiber (g)' },
  { key: 'sodium', label: 'Sodium (mg)' },
];

const createTaggingLog = async (params: {
  dishCatalogId?: string;
  action: 'CREATE' | 'UPDATE';
  changedFields?: Record<string, { before: unknown; after: unknown }>;
  scanImageUrl?: string;
  labelImageUrl?: string;
  aiData?: unknown;
  scanData?: unknown;
  labelData?: unknown;
}) => {
  try {
    await axiosInstance.post(`${API_BASE_URL}/dish-catalog/tagging-logs`, params);
  } catch (err) {
    console.warn('Failed to create tagging log:', err);
  }
};

export const TaggingScan: React.FC = () => {
  const [step, setStep] = useState<Step>('idle');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [labelFile, setLabelFile] = useState<File | null>(null);
  const [labelPreview, setLabelPreview] = useState<string | null>(null);

  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [mergedAiData, setMergedAiData] = useState<Record<string, unknown> | null>(null);
  const [labelResult, setLabelResult] = useState<LabelResult | null>(null);
  const [labelImageUrl, setLabelImageUrl] = useState<string | null>(null);

  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [catalogData, setCatalogData] = useState<CatalogDetail | null>(null);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [saving, setSaving] = useState(false);

  const [recentLogs, setRecentLogs] = useState<Array<{ id: string; action: string; scanImageUrl?: string; aiData?: any; createdAt: string; dishCatalogId?: string }>>([]);

  const fetchRecentLogs = async () => {
    try {
      const res = await axiosInstance.get(`${API_BASE_URL}/dish-catalog/tagging-logs?limit=10`);
      const data = res.data?.data ?? res.data;
      setRecentLogs(data.items ?? []);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchRecentLogs(); }, []);

  const [newRefFile, setNewRefFile] = useState<File | null>(null);
  const [newRefPreview, setNewRefPreview] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [updateForm] = Form.useForm();

  // Populate update form with original values when entering comparison
  useEffect(() => {
    if (step === 'comparison' && catalogData) {
      updateForm.setFieldsValue({
        ...Object.fromEntries(COMPARABLE_FIELDS.map(({ key }) => [key, (catalogData as any)[key] ?? ''])),
        translations: catalogData.translations ?? {},
      });
    }
  }, [step, catalogData]);

  // ─── Photo/Label Upload ──────────────────────

  const handlePhotoUpload = (file: File) => {
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    return false;
  };

  const handleLabelUpload = (file: File) => {
    setLabelFile(file);
    setLabelPreview(URL.createObjectURL(file));
    return false;
  };

  // ─── Analyze ──────────────────────────────────

  const handleAnalyze = async () => {
    if (!photoFile || !labelFile) return;
    setLoading(true);
    setError(null);
    setScanResult(null);
    setMergedAiData(null);
    setLabelResult(null);
    setLabelImageUrl(null);
    setSelectedCandidateId(null);
    setCatalogData(null);
    updateForm.resetFields();

    try {
      const maxDim = await fetchImageMaxDimension();
      const photoBase64 = await resizeAndEncode(photoFile, maxDim);

      // Upload label image first to get a persistent URL for the tagging log
      let uploadedLabelUrl: string | undefined;
      if (labelFile) {
        try {
          const resized = await resizeImageFile(labelFile, maxDim);
          const formData = new FormData();
          formData.append('file', resized);
          const uploadRes = await axiosInstance.post(`${API_BASE_URL}/media/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          uploadedLabelUrl = uploadRes.data?.url;
        } catch (err: any) {
          console.warn('Failed to upload label image:', err.message);
        }
      }

      const [scanRes, labelRes] = await Promise.all([
        axiosInstance.post(`${API_BASE_URL}/dish-catalog/scan`, { imageBase64: photoBase64 }),
        labelFile
          ? axiosInstance.post(`${API_BASE_URL}/dish-catalog/extract-label`, { imageBase64: await resizeAndEncode(labelFile, maxDim) }).catch(() => null)
          : Promise.resolve(null),
      ]);

      const scan: ScanResult = scanRes.data;
      const label: LabelResult | null = labelRes?.data ?? null;
      setScanResult(scan);
      setLabelResult(label);
      if (uploadedLabelUrl) setLabelImageUrl(uploadedLabelUrl);

      // Merge AI suggestion with label data (label overrides)
      const ai: any = { ...scan.aiSuggestion };
      if (label && label.confidence >= 0.3) {
        ai.name = label.productName || ai.name;
        ai.chineseName = label.chineseName || ai.chineseName;
        if (label.chineseNameTraditional) ai.chineseNameTraditional = label.chineseNameTraditional;
        if (label.chineseNameSimplified) ai.chineseNameSimplified = label.chineseNameSimplified;
        if (label.category) ai.category = label.category;
        if (label.type) ai.type = label.type;
        ai.calories = label.calories ?? ai.calories;
        ai.protein = label.protein ?? ai.protein;
        ai.fat = label.fat ?? ai.fat;
        ai.carbs = label.carbs ?? ai.carbs;
        ai.fiber = label.fiber ?? ai.fiber;
        ai.sodium = label.sodium ?? ai.sodium;
      }
      setMergedAiData(ai as Record<string, unknown>);

      // Build translations from Chinese names
      const translations: Record<string, { name: string }> = {};
      if (ai.chineseNameTraditional) translations['zh-HK'] = { name: ai.chineseNameTraditional };
      else if (ai.chineseName) translations['zh-HK'] = { name: ai.chineseName };
      if (ai.chineseNameSimplified) translations['zh-CN'] = { name: ai.chineseNameSimplified };
      else if (ai.chineseName && !translations['zh-CN']) translations['zh-CN'] = { name: ai.chineseName };

      // Pre-fill form for new entry path
      form.setFieldsValue({
        name: ai.name, category: ai.category || '',
        type: ai.type || 'DISH', standardPortionGrams: ai.standardPortionGrams || 100,
        calories: ai.calories ?? 0, protein: ai.protein ?? 0, fat: ai.fat ?? 0,
        carbs: ai.carbs ?? 0, fiber: ai.fiber ?? 0, sodium: ai.sodium ?? 0,
        translations,
        ingredients: (ai.ingredients ?? []).map((ing: any) => ({ displayName: ing.name, weightGrams: ing.estimatedGrams })),
      });

      setStep('edit-form');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  // ─── Catalog Lookup ──────────────────────────

  const loadCatalogDetail = async (id: string) => {
    setSelectedCandidateId(id);
    setLoadingCatalog(true);
    try {
      const res = await axiosInstance.get(`${API_BASE_URL}/dish-catalog/${id}`);
      const item = res.data?.data ?? res.data;
      const catalog: CatalogDetail = {
        id: item.id, name: item.name, category: item.category,
        images: (item.images ?? []).map((img: any) => ({ id: img.id, imageUrl: img.imageUrl })),
        calories: item.calories, protein: item.protein, fat: item.fat, carbs: item.carbs,
        fiber: item.fiber, sodium: item.sodium,
        standardPortionGrams: item.standardPortionGrams,
        type: item.type,
        ingredients: item.ingredients?.map((ing: any) => ({ displayName: ing.displayName, weightGrams: ing.weightGrams })),
        translations: item.translations ?? {},
      };
      setCatalogData(catalog);
      setStep('comparison');
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to load catalog item');
    } finally {
      setLoadingCatalog(false);
    }
  };

  // ─── Update Existing ─────────────────────────

  const handleUpdateExisting = async () => {
    if (!selectedCandidateId || !catalogData) return;
    setSaving(true);
    try {
      const values = await updateForm.validateFields();
      const updateData: Record<string, unknown> = {};
      const changedFields: Record<string, { before: unknown; after: unknown }> = {};

      for (const { key } of COMPARABLE_FIELDS) {
        const formVal = values[key];
        const origVal = (catalogData as any)[key];
        if (formVal !== undefined && String(formVal ?? '') !== String(origVal ?? '')) {
          updateData[key] = formVal;
          changedFields[key] = { before: origVal, after: formVal };
        }
      }

      // Check translations diff
      const origTranslations = catalogData.translations ?? {};
      const formTranslations = values.translations ?? {};
      if (JSON.stringify(origTranslations) !== JSON.stringify(formTranslations)) {
        updateData.translations = formTranslations;
        changedFields.translations = { before: origTranslations, after: formTranslations };
      }

      // Include full label data as nutritionMeta
      if (labelResult) {
        const { confidence, ...labelData } = labelResult;
        updateData.nutritionMeta = labelData;
      }

      if (Object.keys(updateData).length === 0) {
        message.info('No fields changed');
        setSaving(false);
        return;
      }

      await axiosInstance.put(`${API_BASE_URL}/dish-catalog/${selectedCandidateId}`, updateData);

      // Upload all available reference images
      const filesToUpload: File[] = [];
      if (photoFile) filesToUpload.push(photoFile);
      if (labelFile) filesToUpload.push(labelFile);
      if (newRefFile) filesToUpload.push(newRefFile);

      if (filesToUpload.length > 0) {
        const maxDim = await fetchImageMaxDimension();
        message.info({ content: `Uploading ${filesToUpload.length} reference image(s)...`, key: 'uploading' });
        for (const file of filesToUpload) {
          try {
            const resized = await resizeImageFile(file, maxDim);
            const formData = new FormData();
            formData.append('file', resized);
            await axiosInstance.post(`${API_BASE_URL}/dish-catalog/${selectedCandidateId}/images`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          } catch (err: any) {
            console.warn(`Failed to upload ref image: ${err.message}`);
          }
        }
        message.success({ content: `Updated with ${filesToUpload.length} reference image(s)`, key: 'uploading' });
      } else {
        message.success(`Updated ${Object.keys(updateData).length} field(s)`);
      }

      await createTaggingLog({
        dishCatalogId: selectedCandidateId,
        action: 'UPDATE',
        changedFields,
        scanImageUrl: scanResult?.aiSuggestion?.imageUrl,
        labelImageUrl: labelImageUrl ?? undefined,
        aiData: mergedAiData,
        scanData: scanResult,
        labelData: labelResult,
      });

      // Show updated entry preview
      fetchRecentLogs();

      resetState();
    } catch (err: any) {
      if (err.response) message.error(err.response?.data?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  // ─── Create New Entry ────────────────────────

  const handleSaveNew = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      // Store full label data as nutritionMeta
      const nutritionMeta = labelResult
        ? (({ confidence, ...data }) => data)(labelResult)
        : undefined;

      const entry: any = await axiosInstance.post(`${API_BASE_URL}/dish-catalog`, {
        name: values.name, category: values.category || '未分類',
        type: values.type || 'DISH', standardPortionGrams: values.standardPortionGrams,
        calories: values.calories, protein: values.protein, fat: values.fat,
        carbs: values.carbs, fiber: values.fiber, sodium: values.sodium,
        source: 'MANUAL', isActive: true,
        translations: values.translations ?? {},
        nutritionMeta,
        ingredients: (values.ingredients ?? [])
          .filter((ing: any) => ing.displayName?.trim() && ing.weightGrams > 0)
          .map((ing: any, i: number) => ({
            displayName: ing.displayName.trim(), weightGrams: ing.weightGrams, percentage: 0, sortOrder: i,
          })),
      });

      const entryId = entry.data?.id ?? entry.id;

      // Upload photos as reference images
      const photos: File[] = [];
      if (photoFile) photos.push(photoFile);
      if (labelFile) photos.push(labelFile);
      if (newRefFile) photos.push(newRefFile);

      if (photos.length > 0) {
        const maxDim = await fetchImageMaxDimension();
        message.info({ content: `Uploading ${photos.length} reference image(s)...`, key: 'uploading' });
        for (const photo of photos) {
          try {
            const resized = await resizeImageFile(photo, maxDim);
            const formData = new FormData();
            formData.append('file', resized);
            await axiosInstance.post(`${API_BASE_URL}/dish-catalog/${entryId}/images`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          } catch (err: any) {
            console.warn(`Failed to upload ref image: ${err.message}`);
          }
        }
        message.success({ content: `Entry created with ${photos.length} reference image(s)`, key: 'uploading' });
      } else {
        message.success('Catalog entry created');
      }

      await createTaggingLog({
        dishCatalogId: entryId,
        action: 'CREATE',
        scanImageUrl: scanResult?.aiSuggestion?.imageUrl,
        labelImageUrl: labelImageUrl ?? undefined,
        aiData: mergedAiData,
        scanData: scanResult,
        labelData: labelResult,
      });

      // Show saved entry preview
      fetchRecentLogs();

      resetState();
    } catch (err: any) {
      if (err.response) message.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const resetState = () => {
    setStep('idle');
    setPhotoFile(null);
    setPhotoPreview(null);
    setLabelFile(null);
    setLabelPreview(null);
    setScanResult(null);
    setMergedAiData(null);
    setLabelResult(null);
    setLabelImageUrl(null);
    setSelectedCandidateId(null);
    setCatalogData(null);
    setError(null);
    setNewRefFile(null);
    setNewRefPreview(null);
    form.resetFields();
    updateForm.resetFields();
  };

  const replaceAllWithAi = () => {
    if (!mergedAiData) return;
    updateForm.setFieldsValue({
      ...Object.fromEntries(COMPARABLE_FIELDS.map(({ key }) => [key, mergedAiData[key] ?? ''])),
    });
    const trans: Record<string, { name: string }> = { ...(updateForm.getFieldValue('translations') ?? {}) };
    const t = mergedAiData.chineseNameTraditional as string | undefined;
    const s = mergedAiData.chineseNameSimplified as string | undefined;
    const cn = mergedAiData.chineseName as string | undefined;
    if (t || cn) trans['zh-HK'] = { name: t || cn || '' };
    if (s || cn) trans['zh-CN'] = { name: s || cn || '' };
    updateForm.setFieldsValue({ translations: trans });
  };

  const restoreAllToOriginal = () => {
    if (!catalogData) return;
    updateForm.setFieldsValue({
      ...Object.fromEntries(COMPARABLE_FIELDS.map(({ key }) => [key, (catalogData as any)[key] ?? ''])),
      translations: catalogData.translations ?? {},
    });
  };

  const applyAiField = (key: string) => {
    if (!mergedAiData) return;
    updateForm.setFieldsValue({ [key]: mergedAiData[key] ?? '' });
  };

  const restoreField = (key: string) => {
    if (!catalogData) return;
    updateForm.setFieldsValue({ [key]: (catalogData as any)[key] ?? '' });
  };

  // ─── Render ──────────────────────────────────

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
      {/* Step: idle — Upload */}
      {step === 'idle' && (
        <>
        <Card title="Scan to Tag" size="small">
          <Row gutter={24}>
            <Col span={12}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Product Photo (required)</div>
              {photoPreview ? (
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <img src={photoPreview} alt="Product" style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 8 }} />
                  <Button type="text" danger size="small" icon={<DeleteOutlined />}
                    style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(255,255,255,0.8)', borderRadius: 4 }}
                    onClick={() => { setPhotoFile(null); setPhotoPreview(null); }} />
                </div>
              ) : (
                <Upload accept="image/*" showUploadList={false} beforeUpload={handlePhotoUpload} customRequest={() => {}}>
                  <Button icon={<UploadOutlined />} size="large" block>Upload Photo</Button>
                </Upload>
              )}
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Nutrition Label (required)</div>
              {labelPreview ? (
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <img src={labelPreview} alt="Label" style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 8 }} />
                  <Button type="text" danger size="small" icon={<DeleteOutlined />}
                    style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(255,255,255,0.8)', borderRadius: 4 }}
                    onClick={() => { setLabelFile(null); setLabelPreview(null); }} />
                </div>
              ) : (
                <Upload accept="image/*" showUploadList={false} beforeUpload={handleLabelUpload} customRequest={() => {}}>
                  <Button icon={<FileTextOutlined />} size="large" block>Upload Label</Button>
                </Upload>
              )}
            </Col>
          </Row>
          <Divider />
          <Button type="primary" size="large" onClick={handleAnalyze} disabled={!photoFile || !labelFile} loading={loading} block>
            Analyze
          </Button>
        </Card>
        {recentLogs.length > 0 && (
            <Card title="Recent Activity" size="small" style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {recentLogs.map((log) => (
                  <div key={log.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {log.scanImageUrl ? (
                      <img src={log.scanImageUrl} alt="" style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: 40, height: 40, borderRadius: 6, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#999' }}>&#128203;</div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500 }}>
                        <Tag color={log.action === 'CREATE' ? 'green' : 'blue'} style={{ marginRight: 4 }}>{log.action === 'CREATE' ? 'Created' : 'Updated'}</Tag>
                        {log.aiData?.name ?? 'Unknown'}
                      </div>
                      <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                        {new Date(log.createdAt).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {/* Loading */}
      {loading && (
        <Card style={{ marginTop: 16 }}>
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: '#666' }}>Analyzing image{labelFile ? ' and label' : ''}...</div>
          </div>
        </Card>
      )}

      {error && <Alert type="error" message={error} style={{ marginTop: 16 }} />}

      {/* Loading catalog detail */}
      {loadingCatalog && (
        <Card style={{ marginTop: 16 }}><Spin /><span style={{ marginLeft: 12 }}>Loading catalog detail...</span></Card>
      )}

      {/* Step: edit-form — New Entry (default after analyze) */}
      {step === 'edit-form' && scanResult && (
        <>
          {/* Uploaded photos */}
          <Card title="Uploaded" size="small" style={{ marginBottom: 16 }}>
            <Space>
              {photoPreview && <img src={photoPreview} alt="Photo" style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover' }} />}
              {labelPreview && <img src={labelPreview} alt="Label" style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover' }} />}
            </Space>
            <Button style={{ marginLeft: 16 }} onClick={resetState}>Start Over</Button>
          </Card>

          {/* Catalog matches with images */}
          {scanResult.candidates.length > 0 && (
            <Card title={`Existing Entries (${scanResult.candidates.length})`} size="small" style={{ marginBottom: 16 }}>
              <Radio.Group value={selectedCandidateId} style={{ width: '100%' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {scanResult.candidates.map((c) => (
                    <Radio key={c.id} value={c.id} onClick={() => loadCatalogDetail(c.id)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {c.imageUrl && (
                          <img src={c.imageUrl} alt={c.name} style={{ width: 40, height: 40, borderRadius: 4, objectFit: 'cover' }} />
                        )}
                        <Space wrap>
                          <strong>{c.name}</strong>
                          <Tag color={c.matchMethod === 'IMAGE' ? 'geekblue' : 'default'}>{c.matchMethod === 'IMAGE' ? 'Image' : 'Text'}</Tag>
                          <Progress percent={Math.round(c.confidence * 100)} size="small" style={{ width: 80, marginBottom: 0 }}
                            strokeColor={c.confidence >= 0.85 ? '#52c41a' : c.confidence >= 0.75 ? '#faad14' : '#ff4d4f'} />
                          <Tag color={c.source === 'CFS' ? 'orange' : c.source === 'USDA' ? 'blue' : 'green'}>{c.source ?? 'MANUAL'}</Tag>
                          {c.category && <Tag>{c.category}</Tag>}
                        </Space>
                      </div>
                    </Radio>
                  ))}
                </div>
              </Radio.Group>
            </Card>
          )}

          {/* New entry form */}
          <Card title="New Catalog Entry" size="small">
            <Form form={form} layout="vertical" size="small">
              <Form.Item label="Name" name="name" rules={[{ required: true }]}>
                <Input placeholder="Product name" />
              </Form.Item>
              <Form.Item label="Translations" name="translations">
                <TranslationsField fields={[{ key: 'name', label: 'Name', placeholder: 'Translated name' }]} />
              </Form.Item>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="Type" name="type">
                    <Select onChange={() => {
                      // Reset category when type changes since categories are type-specific
                      form.setFieldsValue({ category: undefined });
                    }}>
                      {Object.values(DishType).map(t => (
                        <Select.Option key={t} value={t}>{t.charAt(0) + t.slice(1).toLowerCase()}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Category" name="category" rules={[{ required: true }]}>
                    <Select showSearch placeholder="Select category">
                      {ALL_DISH_CATEGORIES.map(c => (
                        <Select.Option key={c} value={c}>{c.replace(/_/g, ' ')}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Portion (g/ml)" name="standardPortionGrams" rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={1} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left" orientationMargin={0}>Nutrition (per 100g/ml)</Divider>
              <Row gutter={16}>
                <Col span={8}><Form.Item label="Calories (kcal)" name="calories"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
                <Col span={8}><Form.Item label="Protein (g)" name="protein"><InputNumber style={{ width: '100%' }} min={0} step={0.1} /></Form.Item></Col>
                <Col span={8}><Form.Item label="Fat (g)" name="fat"><InputNumber style={{ width: '100%' }} min={0} step={0.1} /></Form.Item></Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}><Form.Item label="Carbs (g)" name="carbs"><InputNumber style={{ width: '100%' }} min={0} step={0.1} /></Form.Item></Col>
                <Col span={8}><Form.Item label="Fiber (g)" name="fiber"><InputNumber style={{ width: '100%' }} min={0} step={0.1} /></Form.Item></Col>
                <Col span={8}><Form.Item label="Sodium (mg)" name="sodium"><InputNumber style={{ width: '100%' }} min={0} step={1} /></Form.Item></Col>
              </Row>

              <Divider orientation="left" orientationMargin={0}>Ingredients</Divider>
              <Form.List name="ingredients">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Row key={key} gutter={8} style={{ marginBottom: 4 }}>
                        <Col span={12}>
                          <Form.Item {...restField} name={[name, 'displayName']} noStyle>
                            <Input placeholder="Ingredient name" size="small" />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item {...restField} name={[name, 'weightGrams']} noStyle>
                            <InputNumber style={{ width: '100%' }} min={0} placeholder="Grams" size="small" />
                          </Form.Item>
                        </Col>
                        <Col span={1}>
                          <Button type="text" danger size="small" onClick={() => remove(name)}>x</Button>
                        </Col>
                      </Row>
                    ))}
                    <Button type="dashed" onClick={() => add()} size="small" icon={<PlusOutlined />}>Add Ingredient</Button>
                  </>
                )}
              </Form.List>

              {(photoPreview || labelPreview || newRefPreview) && (
                <>
                  <Divider orientation="left" orientationMargin={0}>Reference Images</Divider>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {photoPreview && (
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <Image src={photoPreview} alt="Product" width={100} height={100} style={{ borderRadius: 8, objectFit: 'cover' }} />
                        <Button type="text" danger size="small" icon={<DeleteOutlined />}
                          style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(255,255,255,0.85)', borderRadius: 4 }}
                          onClick={() => { setPhotoFile(null); setPhotoPreview(null); }} />
                        <div style={{ fontSize: 11, textAlign: 'center', color: '#666' }}>Product</div>
                      </div>
                    )}
                    {labelPreview && (
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <Image src={labelPreview} alt="Label" width={100} height={100} style={{ borderRadius: 8, objectFit: 'cover' }} />
                        <Button type="text" danger size="small" icon={<DeleteOutlined />}
                          style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(255,255,255,0.85)', borderRadius: 4 }}
                          onClick={() => { setLabelFile(null); setLabelPreview(null); }} />
                        <div style={{ fontSize: 11, textAlign: 'center', color: '#666' }}>Label</div>
                      </div>
                    )}
                    {newRefPreview && (
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <Image src={newRefPreview} alt="New" width={100} height={100} style={{ borderRadius: 8, objectFit: 'cover' }} />
                        <Button type="text" danger size="small" icon={<DeleteOutlined />}
                          style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(255,255,255,0.85)', borderRadius: 4 }}
                          onClick={() => { setNewRefFile(null); setNewRefPreview(null); }} />
                        <div style={{ fontSize: 11, textAlign: 'center', color: '#666' }}>Extra</div>
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Upload accept="image/*" showUploadList={false} beforeUpload={(file) => {
                      setNewRefFile(file);
                      setNewRefPreview(URL.createObjectURL(file));
                      return false;
                    }} customRequest={() => {}}>
                      <Button icon={<PlusOutlined />} size="small">Add Reference Image</Button>
                    </Upload>
                  </div>
                </>
              )}

              {labelResult && (
                <Collapse size="small" style={{ marginTop: 16 }}
                  items={[{
                    key: 'label',
                    label: 'Label Data (auto-saved to nutritionMeta)',
                    children: (
                      <>
                        <Row gutter={16}>
                          {labelResult.servingSize && <Col span={6}><Form.Item label="Serving Size"><Input value={labelResult.servingSize} disabled size="small" /></Form.Item></Col>}
                          {labelResult.sugar > 0 && <Col span={6}><Form.Item label="Sugar (g)"><InputNumber value={labelResult.sugar} disabled size="small" style={{ width: '100%' }} /></Form.Item></Col>}
                        </Row>
                        {labelResult.ingredients && (
                          <Form.Item label="Ingredients"><Input.TextArea value={labelResult.ingredients} disabled size="small" autoSize={{ minRows: 1, maxRows: 4 }} /></Form.Item>
                        )}
                        {labelResult.allergens && (
                          <Form.Item label="Allergens"><Input value={labelResult.allergens} disabled size="small" style={{ color: '#ff4d4f' }} /></Form.Item>
                        )}
                      </>
                    ),
                  }]}
                />
              )}
            </Form>
            <Divider />
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveNew} loading={saving} size="large" block>
              Save New Entry
            </Button>
          </Card>
        </>
      )}

      {/* Step: comparison — Update existing entry */}
      {step === 'comparison' && catalogData && (
        <Card title={`Update: ${catalogData.name}`} size="small"
          extra={<Button onClick={() => setStep('edit-form')}>Back to New Entry</Button>}>
          <div style={{ marginBottom: 12 }}>
            <Space>
              <Button icon={<SwapOutlined />} onClick={replaceAllWithAi} size="small">Fill All with AI</Button>
              <Button icon={<UndoOutlined />} onClick={restoreAllToOriginal} size="small">Restore All</Button>
            </Space>
          </div>

          <Form form={updateForm} layout="vertical" size="small">
            {COMPARABLE_FIELDS.map(({ key, label }) => {
              const origVal = (catalogData as any)[key];
              const aiVal = mergedAiData?.[key];
              const isNumeric = ['standardPortionGrams', 'calories', 'protein', 'fat', 'carbs', 'fiber', 'sodium'].includes(key);
              const isSelect = key === 'type' || key === 'category';
              const changed = String(aiVal ?? '') !== String(origVal ?? '');

              return (
                <Form.Item key={key} label={
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {label}
                    {changed && aiVal != null && (
                      <Tag color="blue" style={{ fontSize: 11, cursor: 'pointer' }}
                        onClick={() => applyAiField(key)}>
                        AI: {String(aiVal)}
                      </Tag>
                    )}
                  </span>
                } style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <Form.Item name={key} noStyle>
                      {isSelect ? (
                        <Select style={{ flex: 1 }} showSearch={key === 'category'}>
                          {key === 'type'
                            ? Object.values(DishType).map(t => (
                                <Select.Option key={t} value={t}>{t.charAt(0) + t.slice(1).toLowerCase()}</Select.Option>
                              ))
                            : ALL_DISH_CATEGORIES.map(c => (
                                <Select.Option key={c} value={c}>{c.replace(/_/g, ' ')}</Select.Option>
                              ))
                          }
                        </Select>
                      ) : isNumeric ? (
                        <InputNumber style={{ flex: 1 }} min={0} step={[key === 'sodium', key === 'standardPortionGrams', key === 'calories'].some(Boolean) ? 1 : 0.1} />
                      ) : (
                        <Input style={{ flex: 1 }} />
                      )}
                    </Form.Item>
                    {changed && (
                      <Tooltip title="Use AI value">
                        <Button type="primary" ghost icon={<SwapOutlined />} onClick={() => applyAiField(key)} />
                      </Tooltip>
                    )}
                    <Tooltip title="Restore original">
                      <Button icon={<UndoOutlined />} onClick={() => restoreField(key)} />
                    </Tooltip>
                  </div>
                </Form.Item>
              );
            })}

            <Divider orientation="left" orientationMargin={0}>Translations</Divider>
            <Form.Item name="translations">
              <TranslationsField fields={[{ key: 'name', label: 'Name', placeholder: 'Translated name' }]} />
            </Form.Item>
          </Form>

          <Divider orientation="left" orientationMargin={0}>Reference Images</Divider>

          {catalogData.images.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              {catalogData.images.map((img) => (
                <div key={img.id} style={{ position: 'relative', display: 'inline-block' }}>
                  <Image src={img.imageUrl} alt="Reference" width={80} height={80}
                    style={{ borderRadius: 6, objectFit: 'cover' }} />
                  <Button type="text" danger size="small" icon={<DeleteOutlined />}
                    style={{ position: 'absolute', top: -6, right: -6, background: '#fff', borderRadius: '50%', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}
                    onClick={async () => {
                      try {
                        await axiosInstance.delete(`${API_BASE_URL}/dish-catalog/${selectedCandidateId}/images/${img.id}`);
                        setCatalogData(prev => prev ? { ...prev, images: prev.images.filter(i => i.id !== img.id) } : prev);
                        message.success('Image removed');
                      } catch { message.error('Failed to remove image'); }
                    }} />
                </div>
              ))}
            </div>
          )}

          {(photoPreview || labelPreview || newRefPreview) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              {photoPreview && (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <Image src={photoPreview} alt="Product" width={80} height={80} style={{ borderRadius: 6, objectFit: 'cover' }} />
                  <Button type="text" danger size="small" icon={<DeleteOutlined />}
                    style={{ position: 'absolute', top: -6, right: -6, background: '#fff', borderRadius: '50%', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}
                    onClick={() => { setPhotoFile(null); setPhotoPreview(null); }} />
                </div>
              )}
              {labelPreview && (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <Image src={labelPreview} alt="Label" width={80} height={80} style={{ borderRadius: 6, objectFit: 'cover' }} />
                  <Button type="text" danger size="small" icon={<DeleteOutlined />}
                    style={{ position: 'absolute', top: -6, right: -6, background: '#fff', borderRadius: '50%', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}
                    onClick={() => { setLabelFile(null); setLabelPreview(null); }} />
                </div>
              )}
              {newRefPreview && (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <Image src={newRefPreview} alt="New" width={80} height={80} style={{ borderRadius: 6, objectFit: 'cover' }} />
                  <Button type="text" danger size="small" icon={<DeleteOutlined />}
                    style={{ position: 'absolute', top: -6, right: -6, background: '#fff', borderRadius: '50%', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}
                    onClick={() => { setNewRefFile(null); setNewRefPreview(null); }} />
                </div>
              )}
            </div>
          )}
          <Upload accept="image/*" showUploadList={false} beforeUpload={(file) => {
            setNewRefFile(file);
            setNewRefPreview(URL.createObjectURL(file));
            return false;
          }} customRequest={() => {}}>
            <Button icon={<PlusOutlined />} size="small" style={{ marginTop: 8 }}>Add New Photo</Button>
          </Upload>

          <Divider />
          <Button type="primary" icon={<SaveOutlined />} onClick={handleUpdateExisting} loading={saving}
            size="large" block>
            Update Record
          </Button>
        </Card>
      )}
    </div>
  );
};
